import React, { useEffect, useMemo, useRef, useState } from 'react';
import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, limit, orderBy, query, setDoc, Timestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../shared/storage/firebase';
import { deriveInvoiceStatus, daysUntil, summarizeInvoiceStatus } from '../../../shared/billing/invoiceStatus';
import { listProgramTerms, programTermForDate, programTermLabelFromId } from '../../../shared/terms/programTerms';
import type { BillingProfile, Invoice, School, SchoolContactWithId, Squad } from '../../../types';
import GoogleMapsService from '../../../shared/services/GoogleMapsService';
import '../styles/AdminInvoicesPage.css';

export type AdminInvoicesPageProps = {
  schools: School[];
  squads: Squad[];
  initialSchoolId?: string | null;
  onConsumedInitialSchoolId?: () => void;
};

type StatusFilter = 'all' | 'draft' | 'needs_pdf' | 'ready_to_send' | 'sent' | 'paid' | 'overdue' | 'late_draft' | 'canceled';
type InvoiceView = 'attention' | 'upcoming' | 'awaiting' | 'overdue' | 'paid' | 'all' | 'legacy';

type DropdownOption = { id: string; label: string };

type GooglePlacePrediction = { placeId: string; description: string };

function addDaysYmd(ymd: string, days: number): string {
  const base = String(ymd || '').trim();
  const n = Number(days);
  if (!base) return '';
  const m = base.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return base;
  const d = new Date(`${base}T00:00:00`);
  if (Number.isNaN(d.getTime())) return base;
  const delta = Number.isFinite(n) ? Math.trunc(n) : 0;
  d.setDate(d.getDate() + delta);
  return d.toISOString().slice(0, 10);
}

function normalizeGoogleLabel(description: string): string {
  const raw = String(description || '').trim();
  return raw
    .replace(/,\s*USA\s*$/i, '')
    .replace(/,\s*United States\s*$/i, '')
    .replace(/,\s*United States of America\s*$/i, '')
    .trim();
}

function extractStateCodeFromLabel(label: string): string | null {
  const parts = String(label || '').split(',').map(s => s.trim()).filter(Boolean);
  for (const seg of parts) {
    const match = seg.toUpperCase().match(/\b([A-Z]{2})\b/);
    if (match && match[1]) return match[1];
  }
  return null;
}

function parseCityFromLabel(label: string): string {
  const cleaned = normalizeGoogleLabel(label);
  const first = cleaned.split(',')[0] || '';
  return String(first).trim();
}

function fmtUsd(centsValue: any): string {
  const n = Number(centsValue);
  if (!Number.isFinite(n)) return '—';
  return `$${(n / 100).toFixed(2)}`;
}

function fmtYmd(ymd: any): string {
  const s = String(ymd || '').trim();
  if (!s) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return s;
}

function plannedSendYmd(invoice: Invoice): string {
  return String((invoice as any).plannedSendDate || invoice.invoiceSendDate || invoice.invoiceDate || '').slice(0, 10);
}

function isFuturePlannedInvoice(invoice: Invoice, today = new Date().toISOString().slice(0, 10)): boolean {
  const planned = plannedSendYmd(invoice);
  return !invoice.sentAt && Boolean(planned) && planned > today;
}

function urgencyRank(status: StatusFilter): number {
  // Lower = more urgent.
  switch (status) {
    case 'overdue': return 0;
    case 'late_draft': return 1;
    case 'ready_to_send': return 2;
    case 'needs_pdf': return 3;
    case 'sent': return 6;
    case 'paid': return 9;
    case 'draft': return 4;
    default: return 7;
  }
}

function SearchableDropdown(props: {
  valueId: string; // selected id, or '' for none
  query: string;   // text in input
  placeholder?: string;
  options: DropdownOption[];
  onQueryChange: (q: string) => void;
  onSelectId: (id: string) => void;
  onClear: () => void;
}): React.ReactElement {
  const { valueId, query, placeholder, options, onQueryChange, onSelectId, onClear } = props;
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number>(-1);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const filtered = useMemo(() => {
    const needle = String(query || '').trim().toLowerCase();
    if (!needle) return options.slice(0, 30);
    return options
      .filter(o => o.label.toLowerCase().includes(needle) || o.id.toLowerCase().includes(needle))
      .slice(0, 30);
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const root = rootRef.current;
      if (!root) return;
      const t = e.target as any;
      if (t && root.contains(t)) return;
      setOpen(false);
      setActiveIdx(-1);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const commitSelect = (id: string) => {
    onSelectId(id);
    setOpen(false);
    setActiveIdx(-1);
  };

  return (
    <div className="searchable-select" ref={rootRef}>
      <div className="searchable-select-input-wrap">
        <input
          className="searchable-select-input"
          value={query}
          placeholder={placeholder}
          onChange={(e) => {
            onQueryChange(e.target.value);
            setOpen(true);
            setActiveIdx(-1);
          }}
          onFocus={() => setOpen(true)}
          onBlur={(e) => {
            // If focus is moving into our menu, keep it open; otherwise close.
            const next = (e.relatedTarget as any) || null;
            const root = rootRef.current;
            if (root && next && root.contains(next)) return;
            setOpen(false);
            setActiveIdx(-1);
          }}
          onKeyDown={(e) => {
            if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
              setOpen(true);
              return;
            }
            if (e.key === 'Escape') {
              setOpen(false);
              setActiveIdx(-1);
              return;
            }
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setOpen(true);
              setActiveIdx(prev => {
                const next = Math.min(filtered.length - 1, (prev < 0 ? 0 : prev + 1));
                return next;
              });
              return;
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActiveIdx(prev => {
                const next = Math.max(0, (prev < 0 ? 0 : prev - 1));
                return next;
              });
              return;
            }
            if (e.key === 'Enter') {
              if (!open) return;
              e.preventDefault();
              const pick = filtered[activeIdx] || null;
              if (pick) commitSelect(pick.id);
              return;
            }
          }}
        />
        {(valueId || query) && (
          <button
            type="button"
            className="searchable-select-clear"
            onClick={() => {
              onClear();
              setOpen(false);
              setActiveIdx(-1);
            }}
            title="Clear"
          >
            ×
          </button>
        )}
      </div>

      {open && filtered.length > 0 && (
        <div
          className="searchable-select-menu"
          onMouseDown={(e) => {
            // prevent input blur before click selection
            e.preventDefault();
          }}
        >
          {filtered.map((opt, idx) => (
            <button
              key={opt.id}
              type="button"
              className={`searchable-select-item ${idx === activeIdx ? 'active' : ''}`}
              onMouseEnter={() => setActiveIdx(idx)}
              onClick={() => commitSelect(opt.id)}
            >
              <span className="searchable-select-item-label">{opt.label}</span>
              <span className="searchable-select-item-meta">{opt.id}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminInvoicesPage(
  { schools, squads, initialSchoolId, onConsumedInitialSchoolId }: AdminInvoicesPageProps
): React.ReactElement {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoiceEra, setInvoiceEra] = useState<'current' | 'legacy'>('current');
  const [invoiceView, setInvoiceView] = useState<InvoiceView>('attention');
  const [globalSearch, setGlobalSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<null | Invoice>(null);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [partnerFilter, setPartnerFilter] = useState<string>('all');
  const [partnerQuery, setPartnerQuery] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>(''); // invoiceDate
  const [dateTo, setDateTo] = useState<string>(''); // invoiceDate
  const [squadSearch, setSquadSearch] = useState<string>(''); // query text for squads covered
  const [squadSelectedId, setSquadSelectedId] = useState<string>(''); // optional exact squad id filter

  const terms = useMemo(() => listProgramTerms({ years: 6 }), []);
  const defaultTermId = useMemo(() => programTermForDate(new Date()).id, []);

  const [createOpen, setCreateOpen] = useState(false);
  const [createStep, setCreateStep] = useState<1 | 2>(1);
  const [createSchoolId, setCreateSchoolId] = useState<string>('');
  const [createTermId, setCreateTermId] = useState<string>(defaultTermId);
  const [createSchoolQuery, setCreateSchoolQuery] = useState<string>('');
  const [createTermQuery, setCreateTermQuery] = useState<string>(programTermLabelFromId(defaultTermId));

  const [createBillingLoading, setCreateBillingLoading] = useState(false);
  const [createBillingProfile, setCreateBillingProfile] = useState<BillingProfile>({});
  const [createBillingProfileBaseline, setCreateBillingProfileBaseline] = useState<BillingProfile>({});
  const [createBillingUsers, setCreateBillingUsers] = useState<SchoolContactWithId[]>([]); // authorized contacts (billing profile)
  const [createPaymentPreferenceQuery, setCreatePaymentPreferenceQuery] = useState<string>('ACH');
  const [createSchoolContacts, setCreateSchoolContacts] = useState<SchoolContactWithId[]>([]);
  const [createAuthorizedContactPickId, setCreateAuthorizedContactPickId] = useState<string>('');
  const [createAuthorizedContactQuery, setCreateAuthorizedContactQuery] = useState<string>('');

  const [createCityQuery, setCreateCityQuery] = useState<string>('');
  const [createStateQuery, setCreateStateQuery] = useState<string>('');
  const [createCityPredictions, setCreateCityPredictions] = useState<GooglePlacePrediction[]>([]);
  const [createStatePredictions, setCreateStatePredictions] = useState<GooglePlacePrediction[]>([]);

  const schoolsById = useMemo(() => {
    const m = new Map<string, School>();
    (schools || []).forEach(s => m.set(String(s.id), s));
    return m;
  }, [schools]);
  const schoolsByLocationId = useMemo(() => {
    const m = new Map<string, School>();
    (schools || []).forEach(s => {
      const locationId = String((s as any)?.locationId || '').trim();
      if (locationId) m.set(locationId, s);
    });
    return m;
  }, [schools]);

  const squadsById = useMemo(() => {
    const m = new Map<string, Squad>();
    (squads || []).forEach(s => m.set(String(s.id), s));
    return m;
  }, [squads]);

  const schoolOptions = useMemo((): DropdownOption[] => {
    return (schools || [])
      .slice()
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
      .map(s => ({ id: String(s.id), label: String(s.name || s.id) }));
  }, [schools]);

  const partnerOptions = useMemo((): DropdownOption[] => {
    const labels = new Map<string, string>();
    invoices.forEach(invoice => {
      if (invoice.legacy === true) return;
      const id = String(invoice.partnerId || '').trim();
      if (!id) return;
      const school = schoolsById.get(String(invoice.schoolId || '')) || schoolsByLocationId.get(String(invoice.locationId || ''));
      const label = String(invoice.payerLegalNameSnapshot || school?.name || invoice.schoolNameSnapshot || id).trim();
      if (!labels.has(id) || labels.get(id) === id) labels.set(id, label);
    });
    return Array.from(labels, ([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [invoices, schoolsById, schoolsByLocationId]);

  const squadOptions = useMemo((): DropdownOption[] => {
    return (squads || [])
      .slice()
      .sort((a, b) => String((a as any).title || '').localeCompare(String((b as any).title || '')))
      .map(s => ({ id: String(s.id), label: String((s as any).title || s.id) }));
  }, [squads]);
  const createCanonicalPartnerId = String((schoolsById.get(createSchoolId) as any)?.partnerId || '').trim();

  const deleteDraftInvoice = async (inv: Invoice) => {
    if (!inv) return;
    if ((inv as any)?.sentAt) {
      setError('This invoice has been sent and cannot be deleted.');
      return;
    }
    setConfirmDelete(inv);
  };

  const confirmDeleteInvoice = async () => {
    const inv = confirmDelete;
    if (!inv) return;
    if ((inv as any)?.sentAt) {
      setError('This invoice has been sent and cannot be deleted.');
      setConfirmDelete(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await deleteDoc(doc(db, 'invoices', inv.id));
      setInvoices(prev => prev.filter(x => x.id !== inv.id));
      setConfirmDelete(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to delete invoice draft.');
    } finally {
      setLoading(false);
    }
  };

  const paymentPreferenceOptions = useMemo((): DropdownOption[] => ([
    { id: 'ach', label: 'ACH' },
    { id: 'check', label: 'Check' },
    { id: 'card', label: 'Card' },
    { id: 'portal', label: 'Portal' },
    { id: 'other', label: 'Other' },
  ]), []);

  const ensureGooglePlacesReady = async (): Promise<void> => {
    if (GoogleMapsService.isApiLoaded()) return;
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey || apiKey === 'YOUR_API_KEY') {
      throw new Error('Google Maps API key not configured (REACT_APP_GOOGLE_MAPS_API_KEY).');
    }
    await GoogleMapsService.load(apiKey);
  };

  const fetchAutocomplete = async (input: string): Promise<GooglePlacePrediction[]> => {
    const q = String(input || '').trim();
    if (q.length < 2) return [];
    await ensureGooglePlacesReady();
    const g = (window as any).google;
    const fetchAutocompleteSuggestions =
      g?.maps?.places?.AutocompleteSuggestion?.fetchAutocompleteSuggestions;

    if (typeof fetchAutocompleteSuggestions === 'function') {
      const resp = await fetchAutocompleteSuggestions({
        input: q,
        includedRegionCodes: ['us'],
      });
      const suggestions = Array.isArray(resp?.suggestions) ? resp.suggestions : [];
      return suggestions
        .slice(0, 12)
        .map((s: any) => {
          const pp = s?.placePrediction ?? s?.prediction ?? s;
          const placeId = String(pp?.placeId || pp?.place_id || '');
          const text =
            pp?.text?.text ||
            pp?.text ||
            pp?.description ||
            pp?.structuredFormat?.mainText?.text ||
            pp?.structured_formatting?.main_text ||
            '';
          const secondary =
            pp?.structuredFormat?.secondaryText?.text ||
            pp?.structured_formatting?.secondary_text ||
            '';
          const description = String(text && secondary ? `${text}, ${secondary}` : text || secondary || '');
          return { placeId, description };
        })
        .filter((p: GooglePlacePrediction) => p.placeId.length > 0 && p.description.length > 0);
    }

    const LegacySvc = g?.maps?.places?.AutocompleteService;
    if (typeof LegacySvc === 'function') {
      const svc = new LegacySvc();
      const res: any = await new Promise((resolve) => {
        svc.getPlacePredictions({ input: q, componentRestrictions: { country: 'us' } }, (preds: any) => resolve(preds || []));
      });
      return (Array.isArray(res) ? res : [])
        .slice(0, 12)
        .map((p: any) => ({ placeId: String(p?.place_id || ''), description: String(p?.description || '') }))
        .filter((p: GooglePlacePrediction) => p.placeId.length > 0 && p.description.length > 0);
    }

    return [];
  };

  useEffect(() => {
    if (!createOpen || createStep !== 2) return;
    const q = String(createCityQuery || '').trim();
    if (q.length < 2) {
      setCreateCityPredictions([]);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(async () => {
      try {
        const preds = await fetchAutocomplete(q);
        if (!cancelled) setCreateCityPredictions(preds);
      } catch (_) {
        if (!cancelled) setCreateCityPredictions([]);
      }
    }, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createCityQuery, createOpen, createStep]);

  useEffect(() => {
    if (!createOpen || createStep !== 2) return;
    const q = String(createStateQuery || '').trim();
    if (q.length < 1) {
      setCreateStatePredictions([]);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(async () => {
      try {
        const preds = await fetchAutocomplete(q);
        if (!cancelled) setCreateStatePredictions(preds);
      } catch (_) {
        if (!cancelled) setCreateStatePredictions([]);
      }
    }, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createStateQuery, createOpen, createStep]);

  const statusOptions = useMemo((): DropdownOption[] => ([
    { id: 'all', label: 'All' },
    { id: 'overdue', label: 'Overdue' },
    { id: 'ready_to_send', label: 'Ready to send' },
    { id: 'needs_pdf', label: 'Needs PDF' },
    { id: 'late_draft', label: 'Late draft' },
    { id: 'draft', label: 'Draft' },
    { id: 'sent', label: 'Sent' },
    { id: 'paid', label: 'Paid' },
  ]), []);

  // Keep partner input in sync with the selected partner.
  useEffect(() => {
    if (partnerFilter === 'all') return;
    const partner = partnerOptions.find(option => option.id === partnerFilter);
    if (partner) setPartnerQuery(partner.label);
  }, [partnerFilter, partnerOptions]);

  // Apply initial school filter when coming from Locations -> Billing.
  useEffect(() => {
    const sid = String(initialSchoolId || '').trim();
    if (!sid) return;
    const s = schoolsById.get(sid);
    const partnerId = String((s as any)?.partnerId || '').trim();
    if (partnerId) {
      setPartnerFilter(partnerId);
      setPartnerQuery(String(s?.name || partnerId));
    }
    if (typeof onConsumedInitialSchoolId === 'function') onConsumedInitialSchoolId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSchoolId, schoolsById]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const snap = await getDocs(
          query(collection(db, 'invoices'), orderBy('createdAt', 'desc'), limit(500))
        );
        const list = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) } as Invoice));
        if (!cancelled) setInvoices(list);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load invoices.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const sNeedle = String(squadSearch || '').trim().toLowerCase();
    const globalNeedle = String(globalSearch || '').trim().toLowerCase();
    const from = String(dateFrom || '').trim();
    const to = String(dateTo || '').trim();

    const matchesSquad = (inv: Invoice): boolean => {
      if (!sNeedle && !squadSelectedId) return true;
      const coveredIds = new Set<string>();
      (inv.coveredSquadIds || []).forEach(id => coveredIds.add(String(id)));
      (inv.lineItems || []).forEach(li => {
        if (li?.squadId) coveredIds.add(String(li.squadId));
      });

      if (squadSelectedId) {
        return coveredIds.has(String(squadSelectedId));
      }

      for (const id of coveredIds) {
        const sq = squadsById.get(id);
        const title = String((sq as any)?.title || '').toLowerCase();
        if (id.toLowerCase().includes(sNeedle) || title.includes(sNeedle)) return true;
      }

      // Fallback: look at line item titles (often snapshot fields)
      for (const li of (inv.lineItems || [])) {
        const t = String(li?.squadTitle || li?.description || '').toLowerCase();
        if (t.includes(sNeedle)) return true;
      }

      return false;
    };

    const matchesDates = (inv: Invoice): boolean => {
      // invoiceDate is typically YYYY-MM-DD (lexicographically sortable)
      const d = String(inv.invoiceDate || '').trim();
      if (!from && !to) return true;
      if (!d) return false;
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    };

    const matchesView = (inv: Invoice): boolean => {
      if (invoiceView === 'legacy') return inv.legacy === true;
      if (inv.legacy === true) return false;
      const summary = summarizeInvoiceStatus(inv);
      if (invoiceView === 'all') return true;
      if (invoiceView === 'attention') return summary.workflow === 'late_draft' || summary.workflow === 'ready_to_send' || (summary.workflow === 'needs_pdf' && !isFuturePlannedInvoice(inv)) || summary.payment === 'overdue';
      if (invoiceView === 'upcoming') return isFuturePlannedInvoice(inv);
      if (invoiceView === 'awaiting') return summary.workflow === 'sent' && ['not_due', 'due_soon', 'partial'].includes(summary.payment);
      if (invoiceView === 'overdue') return summary.payment === 'overdue';
      if (invoiceView === 'paid') return summary.payment === 'paid' || summary.workflow === 'paid';
      return true;
    };

    const matchesGlobalSearch = (inv: Invoice): boolean => {
      if (!globalNeedle) return true;
      const school = schoolsById.get(String(inv.schoolId || '')) || schoolsByLocationId.get(String(inv.locationId || ''));
      const squadLabels = [...(inv.coveredSquadIds || []), ...(inv.lineItems || []).map(item => item.squadId || '')]
        .map(id => String((squadsById.get(String(id)) as any)?.title || id));
      return [
        inv.internalInvoiceNumberSnapshot,
        inv.payerLegalNameSnapshot,
        inv.schoolNameSnapshot,
        inv.partnerId,
        school?.name,
        ...squadLabels,
      ].some(value => String(value || '').toLowerCase().includes(globalNeedle));
    };

    const list = invoices
      .filter(inv => {
        if (!matchesView(inv)) return false;
        if (partnerFilter !== 'all' && String(inv.partnerId || '') !== partnerFilter) return false;
        if (statusFilter !== 'all') {
          const derived = deriveInvoiceStatus(inv) as StatusFilter;
          if (derived !== statusFilter) return false;
        }
        if (!matchesDates(inv)) return false;
        if (!matchesSquad(inv)) return false;
        if (!matchesGlobalSearch(inv)) return false;
        return true;
      })
      .slice();

    // Sort: “relevance + urgency” (status first, then due date proximity, then recency)
    list.sort((a, b) => {
      const sa = deriveInvoiceStatus(a) as StatusFilter;
      const sb = deriveInvoiceStatus(b) as StatusFilter;
      const ra = urgencyRank(sa);
      const rb = urgencyRank(sb);
      if (ra !== rb) return ra - rb;

      const da = daysUntil((a as any).paymentDueDate || (a as any).invoiceSendDate || (a as any).invoiceDate);
      const dbb = daysUntil((b as any).paymentDueDate || (b as any).invoiceSendDate || (b as any).invoiceDate);
      if (da != null && dbb != null && da !== dbb) return da - dbb;

      const ca = (a as any).createdAt?.toMillis?.() ?? 0;
      const cb = (b as any).createdAt?.toMillis?.() ?? 0;
      return cb - ca;
    });

    return list;
  }, [dateFrom, dateTo, globalSearch, invoiceView, invoices, partnerFilter, schoolsById, schoolsByLocationId, squadSearch, squadSelectedId, squadsById, statusFilter]);

  const viewCounts = useMemo(() => {
    const counts: Record<InvoiceView, number> = { attention: 0, upcoming: 0, awaiting: 0, overdue: 0, paid: 0, all: 0, legacy: 0 };
    invoices.forEach(inv => {
      if (inv.legacy === true) {
        counts.legacy += 1;
        return;
      }
      counts.all += 1;
      const summary = summarizeInvoiceStatus(inv);
      if (summary.workflow === 'late_draft' || summary.workflow === 'ready_to_send' || (summary.workflow === 'needs_pdf' && !isFuturePlannedInvoice(inv)) || summary.payment === 'overdue') counts.attention += 1;
      if (isFuturePlannedInvoice(inv)) counts.upcoming += 1;
      if (summary.workflow === 'sent' && ['not_due', 'due_soon', 'partial'].includes(summary.payment)) counts.awaiting += 1;
      if (summary.payment === 'overdue') counts.overdue += 1;
      if (summary.payment === 'paid' || summary.workflow === 'paid') counts.paid += 1;
    });
    return counts;
  }, [invoices]);

  const selectView = (view: InvoiceView) => {
    setInvoiceView(view);
    setInvoiceEra(view === 'legacy' ? 'legacy' : 'current');
    setStatusFilter('all');
  };

  const closeCreate = () => {
    setCreateOpen(false);
    setCreateStep(1);
  };

  const loadCreateBillingTerms = async (schoolId: string) => {
    const sid = String(schoolId || '').trim();
    if (!sid) return;
    setCreateBillingLoading(true);
    setError(null);
    try {
      const selectedSchool = schoolsById.get(sid);
      const partnerId = String((selectedSchool as any)?.partnerId || '').trim();
      if (!partnerId) {
        console.warn('[BillingCutover] Loading historical school billing and contacts read-only', {
          schoolId: sid,
          action: 'read-only',
        });
      }
      const profilePromise = partnerId
        ? getDoc(doc(db, 'partners', partnerId))
        : getDoc(doc(db, 'schools', sid, 'billing', 'profile'));
      const contactsRef = partnerId
        ? collection(db, 'partners', partnerId, 'contacts')
        : collection(db, 'schools', sid, 'contacts');
      const [profSnap, contactsSnap] = await Promise.all([
        profilePromise,
        getDocs(contactsRef),
      ]);
      const prof = (profSnap.exists()
        ? (partnerId ? ((profSnap.data() as any)?.billingDefaults || {}) : (profSnap.data() as any))
        : {}) as BillingProfile;
      const allContacts = contactsSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as SchoolContactWithId[];
      allContacts.sort((a, b) => String(a.displayName || '').localeCompare(String(b.displayName || '')));
      setCreateSchoolContacts(allContacts);
      const authorizedIds = Array.isArray((prof as any)?.authorizedContactIds)
        ? ((prof as any).authorizedContactIds as any[]).map(x => String(x || '').trim()).filter(Boolean)
        : [];
      const byId = new Map<string, SchoolContactWithId>();
      allContacts.forEach(c => {
        byId.set(String(c.id), c);
        const legacyIds = Array.isArray((c as any)?.legacyContactIds) ? (c as any).legacyContactIds : [];
        legacyIds.forEach((id: unknown) => {
          const cleanId = String(id || '').trim();
          if (cleanId) byId.set(cleanId, c);
        });
      });
      const authorizedContacts = authorizedIds.map(id => byId.get(id)).filter(Boolean) as SchoolContactWithId[];
      setCreateBillingProfile(prof);
      setCreateBillingProfileBaseline(prof);
      setCreateBillingUsers(authorizedContacts);
      setCreateAuthorizedContactPickId('');
      setCreateAuthorizedContactQuery('');
      const pref = String((prof as any)?.paymentPreference || 'ach');
      setCreatePaymentPreferenceQuery(pref.toUpperCase());
      const addr: any = (prof as any)?.billingAddress || {};
      const city = String(addr?.city || '').trim();
      const state = String(addr?.state || '').trim();
      setCreateCityQuery(city && state ? `${city}, ${state}` : city);
      setCreateStateQuery(state);
    } finally {
      setCreateBillingLoading(false);
    }
  };

  useEffect(() => {
    if (!createOpen) return;
    if (createStep !== 2) return;
    if (!createSchoolId) return;
    loadCreateBillingTerms(createSchoolId).catch((e: any) => setError(e?.message || 'Failed to load billing terms.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOpen, createStep, createSchoolId]);

  const createTermsDirty = useMemo(() => {
    const p = createBillingProfile || {};
    const b = createBillingProfileBaseline || {};
    const addrP: any = (p as any).billingAddress || {};
    const addrB: any = (b as any).billingAddress || {};
    return JSON.stringify({
      legalName: String((p as any).legalName || ''),
      paymentPreference: String((p as any).paymentPreference || ''),
      netInvoiceDays: Number((p as any).netInvoiceDays ?? 0),
      netPaymentDays: Number((p as any).netPaymentDays ?? 0),
      billingAddress: {
        line1: String(addrP.line1 || ''),
        city: String(addrP.city || ''),
        state: String(addrP.state || ''),
        postalCode: String(addrP.postalCode || ''),
      },
    }) !== JSON.stringify({
      legalName: String((b as any).legalName || ''),
      paymentPreference: String((b as any).paymentPreference || ''),
      netInvoiceDays: Number((b as any).netInvoiceDays ?? 0),
      netPaymentDays: Number((b as any).netPaymentDays ?? 0),
      billingAddress: {
        line1: String(addrB.line1 || ''),
        city: String(addrB.city || ''),
        state: String(addrB.state || ''),
        postalCode: String(addrB.postalCode || ''),
      },
    });
  }, [createBillingProfile, createBillingProfileBaseline]);

  const approvedRecipientsCount = useMemo(() => {
    return createBillingUsers.filter(c => String((c as any).email || '').trim()).length;
  }, [createBillingUsers]);

  const requiredMissing = useMemo(() => {
    const addr: any = (createBillingProfile as any)?.billingAddress || {};
    const missing: string[] = [];
    if (!String((createBillingProfile as any)?.legalName || '').trim()) missing.push('legal name');
    if (!String(addr?.line1 || '').trim()) missing.push('billing address line 1');
    if (!String(addr?.city || '').trim()) missing.push('city');
    if (!String(addr?.state || '').trim()) missing.push('state');
    if (!String(addr?.postalCode || '').trim()) missing.push('postal code');
    if (approvedRecipientsCount <= 0) missing.push('at least 1 authorized billing contact (with email)');
    return missing;
  }, [createBillingProfile, approvedRecipientsCount]);

  const saveCreateBillingProfile = async () => {
    const sid = String(createSchoolId || '').trim();
    if (!sid) return;
    if (!createTermsDirty) return;
    const partnerId = String((schoolsById.get(sid) as any)?.partnerId || '').trim();
    if (!partnerId) throw new Error('Historical billing is read-only. Map this school to a partner before editing or creating an invoice.');
    await setDoc(doc(db, 'partners', partnerId), {
      billingDefaults: createBillingProfile,
      updatedAt: Timestamp.now(),
      updatedBy: 'admin',
    } as any, { merge: true });
    setCreateBillingProfileBaseline(createBillingProfile);
  };

  const setCreateAuthorizedContactIds = async (nextIds: string[]) => {
    const sid = String(createSchoolId || '').trim();
    if (!sid) return;
    const partnerId = String((schoolsById.get(sid) as any)?.partnerId || '').trim();
    if (!partnerId) throw new Error('Historical billing is read-only. Map this school to a partner before changing recipients.');
    const billingContactId = String((createBillingProfile as any)?.billingContactId || '').trim();
    const safeIds = (Array.isArray(nextIds) ? nextIds : []).map(x => String(x || '').trim()).filter(Boolean);
    const uniqueIds = Array.from(new Set(safeIds));
    const billingPatch = {
      ...(createBillingProfile as any),
      authorizedContactIds: uniqueIds,
      ...(billingContactId && !uniqueIds.includes(billingContactId) ? { billingContactId: '' } : {}),
    };
    await setDoc(doc(db, 'partners', partnerId), {
      billingDefaults: billingPatch,
      updatedAt: Timestamp.now(),
      updatedBy: 'admin',
    } as any, { merge: true });
  };

  const addCreateAuthorizedContact = async () => {
    const sid = String(createSchoolId || '').trim();
    const pickId = String(createAuthorizedContactPickId || '').trim();
    if (!sid) return;
    if (!pickId) return;
    const existingIds = Array.isArray((createBillingProfile as any)?.authorizedContactIds)
      ? ((createBillingProfile as any).authorizedContactIds as any[]).map(x => String(x || '').trim()).filter(Boolean)
      : createBillingUsers.map(c => String(c.id || '').trim()).filter(Boolean);
    if (existingIds.includes(pickId)) {
      setCreateAuthorizedContactPickId('');
      setCreateAuthorizedContactQuery('');
      return;
    }
    setCreateBillingLoading(true);
    try {
      await setCreateAuthorizedContactIds([...existingIds, pickId]);
      await loadCreateBillingTerms(sid);
    } catch (e: any) {
      setError(e?.message || 'Failed to add authorized contact.');
    } finally {
      setCreateBillingLoading(false);
    }
  };

  const removeCreateAuthorizedContact = async (contactId: string) => {
    const sid = String(createSchoolId || '').trim();
    const cid = String(contactId || '').trim();
    if (!sid) return;
    if (!cid) return;
    const existingIds = Array.isArray((createBillingProfile as any)?.authorizedContactIds)
      ? ((createBillingProfile as any).authorizedContactIds as any[]).map(x => String(x || '').trim()).filter(Boolean)
      : createBillingUsers.map(c => String(c.id || '').trim()).filter(Boolean);
    const next = existingIds.filter(x => x !== cid);
    setCreateBillingLoading(true);
    try {
      await setCreateAuthorizedContactIds(next);
      await loadCreateBillingTerms(sid);
    } catch (e: any) {
      setError(e?.message || 'Failed to remove authorized contact.');
    } finally {
      setCreateBillingLoading(false);
    }
  };

  const goCreateNext = async () => {
    if (!createSchoolId) {
      setError('Pick a location first.');
      return;
    }
    setError(null);
    setCreateStep(2);
  };

  const confirmCreateInvoice = async () => {
    const schoolId = String(createSchoolId || '').trim();
    const termId = String(createTermId || '').trim();
    if (!schoolId) return;

    try {
      setLoading(true);
      setError(null);

      const school = schoolsById.get(schoolId);
      const partnerId = String((school as any)?.partnerId || '').trim();
      const locationId = String((school as any)?.locationId || '').trim();
      if (!partnerId) {
        setError('Historical billing is read-only. Map this school to a partner before creating an invoice.');
        return;
      }
      if (requiredMissing.length > 0) {
        setError(`Please confirm: ${requiredMissing.join(', ')}.`);
        return;
      }
      await saveCreateBillingProfile();

      const today = new Date().toISOString().slice(0, 10);
      const netInvoiceDays = Number.isFinite(Number((createBillingProfile as any)?.netInvoiceDays)) ? Number((createBillingProfile as any)?.netInvoiceDays) : 0;
      const netPaymentDays = Number.isFinite(Number((createBillingProfile as any)?.netPaymentDays)) ? Number((createBillingProfile as any)?.netPaymentDays) : 0;
      const invoiceSendDate = addDaysYmd(today, netInvoiceDays);
      const invoiceDate = invoiceSendDate;
      const paymentDueDate = addDaysYmd(invoiceDate, netPaymentDays);

      const ref = await addDoc(collection(db, 'invoices'), {
        legacy: false,
        partnerId,
        ...(locationId ? { locationId } : {}),
        termId: termId || null,
        schoolNameSnapshot: String(school?.name || schoolId),
        schoolTermSnapshot: termId ? programTermLabelFromId(termId) : null,
        invoiceDate,
        invoiceSendDate,
        paymentDueDate,
        payerLegalNameSnapshot: String((createBillingProfile as any)?.legalName || school?.name || schoolId),
        payerBillingAddressSnapshot: (createBillingProfile as any)?.billingAddress || {},
        netInvoiceSnapshot: netInvoiceDays,
        netPaymentSnapshot: netPaymentDays,
        status: 'draft',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      } as Partial<Invoice>);
      setCreateOpen(false);
      setCreateStep(1);
      navigate(`/app/admin/invoices/${ref.id}`);
    } catch (e: any) {
      setError(e?.message || 'Failed to create invoice.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-invoices-page">
      <div className="admin-invoices-header">
        <div>
          <h2 style={{ margin: 0 }}>Partner invoices</h2>
          <div className="admin-invoices-subtitle">Review upcoming invoices, send approved drafts, and track partner payments.</div>
        </div>
        <div className="admin-invoices-header-actions">
          <button
            type="button"
            className="admin-invoices-tool-btn"
            onClick={() => navigate('/app/admin/invoice-template')}
            title="Manage invoice template, logo, W-9/ACH, email defaults"
          >
            <span className="admin-invoices-tool-icon">🧾</span>
            Settings
          </button>
          <button
            type="button"
            className="admin-invoices-tool-btn"
            onClick={() => navigate('/app/admin/record-payment')}
          >
            Record payment
          </button>
          <button
            type="button"
            className="admin-invoices-tool-btn admin-invoices-tool-btn--primary"
            onClick={() => navigate('/app/admin', { state: { adminTab: 'users', adminUserTab: 'partners' } })}
            title="Select a partner to create an invoice"
          >
            + Create invoice
          </button>
        </div>
      </div>

      {createOpen && (
        <div className="admin-invoices-modal-overlay" onClick={closeCreate}>
          <div className="admin-invoices-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-invoices-modal-title">
              Create invoice
              <span className="admin-invoices-modal-step">Step {createStep} of 2</span>
            </div>

            {createStep === 1 ? (
              <div className="admin-invoices-modal-body">
                <label>
                  <span>Location</span>
                  <SearchableDropdown
                    valueId={createSchoolId}
                    query={createSchoolQuery}
                    placeholder="Select a location…"
                    options={schoolOptions}
                    onQueryChange={(q) => {
                      setCreateSchoolQuery(q);
                      if (!String(q || '').trim()) setCreateSchoolId('');
                    }}
                    onSelectId={(id) => {
                      setCreateSchoolId(id);
                      const s = schoolsById.get(String(id));
                      setCreateSchoolQuery(String(s?.name || id));
                    }}
                    onClear={() => {
                      setCreateSchoolId('');
                      setCreateSchoolQuery('');
                    }}
                  />
                </label>

                <label>
                  <span>Term (optional)</span>
                  <SearchableDropdown
                    valueId={createTermId}
                    query={createTermQuery}
                    placeholder="None (custom)…"
                    options={[{ id: '', label: 'None (custom)' }, ...terms.map(t => ({ id: t.id, label: t.label }))]}
                    onQueryChange={(q) => {
                      setCreateTermQuery(q);
                      if (!String(q || '').trim()) setCreateTermId('');
                    }}
                    onSelectId={(id) => {
                      setCreateTermId(id);
                      if (!id) {
                        setCreateTermQuery('None (custom)');
                      } else {
                        setCreateTermQuery(programTermLabelFromId(id));
                      }
                    }}
                    onClear={() => {
                      setCreateTermId('');
                      setCreateTermQuery('None (custom)');
                    }}
                  />
                </label>
              </div>
            ) : (
              <div className="admin-invoices-modal-body admin-invoices-modal-body--stack">
                <div className="admin-invoices-modal-hint" style={{ gridColumn: '1 / -1' }}>
                  Confirm the billing profile and authorized recipients for this location before creating the invoice.
                </div>
                {!createCanonicalPartnerId && (
                  <div className="admin-invoices-modal-warn" style={{ gridColumn: '1 / -1' }}>
                    Historical school billing is read-only. Map this school to a canonical partner/location before editing billing or creating an invoice.
                  </div>
                )}

                {requiredMissing.length > 0 && (
                  <div className="admin-invoices-modal-warn" style={{ gridColumn: '1 / -1' }}>
                    Missing: {requiredMissing.join(', ')}.
                  </div>
                )}

                <fieldset disabled={!createCanonicalPartnerId} style={{ display: 'contents' }}>
                <div className="admin-invoices-modal-subtitle" style={{ gridColumn: '1 / -1' }}>
                  Billing profile
                </div>

                <div className="admin-invoices-modal-profile-grid" style={{ gridColumn: '1 / -1' }}>
                  <label className="admin-invoices-modal-profile-legal">
                    <span>Legal name</span>
                    <input
                      className="admin-invoices-modal-input"
                      value={(createBillingProfile as any)?.legalName || ''}
                      onChange={(e) => setCreateBillingProfile(p => ({ ...p, legalName: e.target.value }))}
                      disabled={loading || createBillingLoading}
                    />
                  </label>

                  <label className="admin-invoices-modal-profile-pref">
                    <span>Payment preference</span>
                    <SearchableDropdown
                      valueId={String((createBillingProfile as any)?.paymentPreference || 'ach')}
                      query={createPaymentPreferenceQuery}
                      placeholder="ACH"
                      options={paymentPreferenceOptions}
                      onQueryChange={(q) => {
                        setCreatePaymentPreferenceQuery(q);
                        if (!String(q || '').trim()) {
                          setCreateBillingProfile(p => ({ ...p, paymentPreference: 'ach' as any }));
                        }
                      }}
                      onSelectId={(id) => {
                        setCreateBillingProfile(p => ({ ...p, paymentPreference: id as any }));
                        const opt = paymentPreferenceOptions.find(o => o.id === id);
                        setCreatePaymentPreferenceQuery(opt?.label || String(id).toUpperCase());
                      }}
                      onClear={() => {
                        setCreateBillingProfile(p => ({ ...p, paymentPreference: 'ach' as any }));
                        setCreatePaymentPreferenceQuery('ACH');
                      }}
                    />
                  </label>

                  <label className="admin-invoices-modal-profile-net-invoice">
                    <span>Net days until invoice</span>
                    <input
                      className="admin-invoices-modal-input admin-invoices-modal-input--narrow"
                      type="number"
                      value={(createBillingProfile as any)?.netInvoiceDays ?? ''}
                      onChange={(e) => setCreateBillingProfile(p => ({ ...p, netInvoiceDays: e.target.value === '' ? undefined : Number(e.target.value) }))}
                      disabled={loading || createBillingLoading}
                    />
                  </label>

                  <label className="admin-invoices-modal-profile-net-payment">
                    <span>Net days until payment</span>
                    <input
                      className="admin-invoices-modal-input admin-invoices-modal-input--narrow"
                      type="number"
                      value={(createBillingProfile as any)?.netPaymentDays ?? ''}
                      onChange={(e) => setCreateBillingProfile(p => ({ ...p, netPaymentDays: e.target.value === '' ? undefined : Number(e.target.value) }))}
                      disabled={loading || createBillingLoading}
                    />
                  </label>
                </div>

                <label style={{ gridColumn: '1 / -1' }}>
                  <span>Billing address line 1</span>
                  <input
                    className="admin-invoices-modal-input"
                    value={(createBillingProfile as any)?.billingAddress?.line1 || ''}
                    onChange={(e) => setCreateBillingProfile(p => ({ ...p, billingAddress: { ...(p.billingAddress || {}), line1: e.target.value } }))}
                    disabled={loading || createBillingLoading}
                  />
                </label>

                <label>
                  <span>City</span>
                  <SearchableDropdown
                    valueId=""
                    query={createCityQuery}
                    placeholder="Search city…"
                    options={createCityPredictions.map(p => ({ id: p.placeId, label: p.description }))}
                    onQueryChange={(q) => {
                      setCreateCityQuery(q);
                      // keep city editable even without selecting suggestion
                      setCreateBillingProfile(p => ({ ...p, billingAddress: { ...(p.billingAddress || {}), city: q } }));
                    }}
                    onSelectId={(id) => {
                      const picked = createCityPredictions.find(p => p.placeId === id);
                      const label = picked ? picked.description : '';
                      const city = parseCityFromLabel(label);
                      const st = extractStateCodeFromLabel(label);
                      setCreateCityQuery(st ? `${city}, ${st}` : city);
                      if (st) setCreateStateQuery(st);
                      setCreateBillingProfile(p => ({
                        ...p,
                        billingAddress: {
                          ...(p.billingAddress || {}),
                          city,
                          ...(st ? { state: st } : {}),
                        },
                      }));
                    }}
                    onClear={() => {
                      setCreateCityQuery('');
                      setCreateBillingProfile(p => ({ ...p, billingAddress: { ...(p.billingAddress || {}), city: '' } }));
                    }}
                  />
                </label>

                <label>
                  <span>State</span>
                  <SearchableDropdown
                    valueId=""
                    query={createStateQuery}
                    placeholder="Search state…"
                    options={createStatePredictions.map(p => ({ id: p.placeId, label: p.description }))}
                    onQueryChange={(q) => {
                      setCreateStateQuery(q);
                      setCreateBillingProfile(p => ({ ...p, billingAddress: { ...(p.billingAddress || {}), state: q } }));
                    }}
                    onSelectId={(id) => {
                      const picked = createStatePredictions.find(p => p.placeId === id);
                      const label = picked ? picked.description : '';
                      const st = extractStateCodeFromLabel(label) || String(label).trim().slice(0, 2).toUpperCase();
                      setCreateStateQuery(st);
                      setCreateBillingProfile(p => ({ ...p, billingAddress: { ...(p.billingAddress || {}), state: st } }));
                    }}
                    onClear={() => {
                      setCreateStateQuery('');
                      setCreateBillingProfile(p => ({ ...p, billingAddress: { ...(p.billingAddress || {}), state: '' } }));
                    }}
                  />
                </label>

                <label>
                  <span>Postal code</span>
                  <input
                    className="admin-invoices-modal-input"
                    value={(createBillingProfile as any)?.billingAddress?.postalCode || ''}
                    onChange={(e) => setCreateBillingProfile(p => ({ ...p, billingAddress: { ...(p.billingAddress || {}), postalCode: e.target.value } }))}
                    disabled={loading || createBillingLoading}
                  />
                </label>

                <div className="admin-invoices-modal-subtitle" style={{ gridColumn: '1 / -1' }}>
                  Authorized contacts ({approvedRecipientsCount} with email)
                </div>

                <div className="admin-invoices-modal-recipients" style={{ gridColumn: '1 / -1' }}>
                  {createBillingLoading ? (
                    <div className="muted">Loading…</div>
                  ) : createBillingUsers.length === 0 ? (
                    <div className="muted">No authorized contacts yet.</div>
                  ) : (
                    <div className="admin-invoices-modal-recipient-list">
                      {createBillingUsers.map(c => (
                        <div key={c.id} className="admin-invoices-modal-recipient-row">
                          <span className="admin-invoices-modal-recipient-email">{c.displayName || c.id}</span>
                          <span className="admin-invoices-modal-recipient-status">{String((c as any).email || '').trim() || 'no email'}</span>
                          <span style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              className="admin-invoices-modal-add-btn"
                              disabled
                              title="Edit canonical contacts from Partner details."
                              style={{ padding: '6px 10px' }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="admin-invoices-modal-add-btn"
                              onClick={() => void removeCreateAuthorizedContact(c.id)}
                              disabled={loading || createBillingLoading || !createCanonicalPartnerId}
                              title="Remove authorized contact"
                              style={{ padding: '6px 10px' }}
                            >
                              Remove
                            </button>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="admin-invoices-modal-add-recipient">
                    <div style={{ flex: 1 }}>
                      <SearchableDropdown
                        valueId={createAuthorizedContactPickId}
                        query={createAuthorizedContactQuery}
                        placeholder="Search school contacts…"
                        options={createSchoolContacts.map(c => ({
                          id: String(c.id),
                          label: `${String(c.displayName || '').trim() || '(no name)'}${String((c as any).email || '').trim() ? ` — ${String((c as any).email || '').trim()}` : ''}`,
                        }))}
                        onQueryChange={setCreateAuthorizedContactQuery}
                        onSelectId={(id) => {
                          setCreateAuthorizedContactPickId(id);
                          const picked = createSchoolContacts.find(c => String(c.id) === String(id));
                          const label = picked
                            ? `${String(picked.displayName || '').trim() || '(no name)'}${String((picked as any).email || '').trim() ? ` — ${String((picked as any).email || '').trim()}` : ''}`
                            : id;
                          setCreateAuthorizedContactQuery(label);
                        }}
                        onClear={() => {
                          setCreateAuthorizedContactPickId('');
                          setCreateAuthorizedContactQuery('');
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      className="admin-invoices-modal-add-btn"
                      onClick={addCreateAuthorizedContact}
                      disabled={loading || createBillingLoading || !createCanonicalPartnerId || !String(createAuthorizedContactPickId || '').trim()}
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      className="admin-invoices-modal-add-btn"
                      disabled
                      title="Create canonical contacts from Partner details."
                    >
                      New contact
                    </button>
                  </div>

                  {createTermsDirty && (
                    <div className="admin-invoices-modal-note">
                      Terms changed — they’ll be saved when you create the invoice.
                    </div>
                  )}
                </div>
                </fieldset>
              </div>
            )}

            <div className="admin-invoices-modal-actions">
              <button className="admin-invoices-modal-cancel" onClick={closeCreate} disabled={loading}>
                Cancel
              </button>
              {createStep === 1 ? (
                <button
                  className="admin-invoices-modal-create"
                  onClick={goCreateNext}
                  disabled={loading || !createSchoolId}
                >
                  Next
                </button>
              ) : (
                <>
                  <button
                    className="admin-invoices-modal-back"
                    onClick={() => setCreateStep(1)}
                    disabled={loading}
                  >
                    Back
                  </button>
                  <button
                    className="admin-invoices-modal-create"
                    onClick={confirmCreateInvoice}
                    disabled={loading || createBillingLoading || !createSchoolId || !createCanonicalPartnerId}
                  >
                    Confirm &amp; create
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="admin-invoices-summary" aria-label="Invoice work views">
        {([
          ['attention', 'Needs attention', 'Drafts blocked or ready for action'],
          ['upcoming', 'Upcoming', 'Scheduled drafts'],
          ['awaiting', 'Awaiting payment', 'Sent and still open'],
          ['overdue', 'Overdue', 'Sent, positive balance, past due'],
          ['paid', 'Paid', 'Completed current invoices'],
        ] as Array<[InvoiceView, string, string]>).map(([view, label, hint]) => (
          <button key={view} type="button" className={`admin-invoices-summary-card${invoiceView === view ? ' is-active' : ''}`} onClick={() => selectView(view)}>
            <span>{label}</span><strong>{viewCounts[view]}</strong><small>{hint}</small>
          </button>
        ))}
      </div>

      <div className="admin-invoices-searchbar">
        <div className="admin-invoices-search-wrap">
          <span aria-hidden="true">⌕</span>
          <input
            value={globalSearch}
            onChange={(event) => setGlobalSearch(event.target.value)}
            placeholder="Search partner, invoice number, location, or squad…"
            aria-label="Search invoices"
          />
          {globalSearch && <button type="button" onClick={() => setGlobalSearch('')} aria-label="Clear search">×</button>}
        </div>
        <button type="button" className={`admin-invoices-filter-toggle${filtersOpen ? ' is-active' : ''}`} onClick={() => setFiltersOpen(value => !value)}>
          Filters {filtersOpen ? '−' : '+'}
        </button>
        <button type="button" className={invoiceView === 'all' ? 'is-active' : ''} onClick={() => selectView('all')}>All current <b>{viewCounts.all}</b></button>
        <button type="button" className={invoiceView === 'legacy' ? 'is-active' : ''} onClick={() => selectView('legacy')}>Legacy archive <b>{viewCounts.legacy}</b></button>
      </div>

      {filtersOpen && <div className="admin-invoices-filters">
        <label>
          <span>Status</span>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}>
            {statusOptions.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
          </select>
        </label>

        <label>
          <span>Partner</span>
          <SearchableDropdown
            valueId={partnerFilter === 'all' ? '' : partnerFilter}
            query={partnerQuery}
            placeholder="All partners…"
            options={partnerOptions}
            onQueryChange={(q) => {
              setPartnerQuery(q);
              if (!String(q || '').trim()) setPartnerFilter('all');
            }}
            onSelectId={(id) => {
              setPartnerFilter(id);
              const partner = partnerOptions.find(option => option.id === id);
              setPartnerQuery(partner?.label || id);
            }}
            onClear={() => {
              setPartnerFilter('all');
              setPartnerQuery('');
            }}
          />
        </label>

        <label className="admin-invoices-squad-filter">
          <span>Squads covered</span>
          <SearchableDropdown
            valueId={squadSelectedId}
            query={squadSearch}
            placeholder="Search squads…"
            options={squadOptions}
            onQueryChange={(q) => {
              setSquadSearch(q);
              setSquadSelectedId('');
            }}
            onSelectId={(id) => {
              setSquadSelectedId(id);
              const sq = squadsById.get(String(id));
              setSquadSearch(String((sq as any)?.title || id));
            }}
            onClear={() => {
              setSquadSelectedId('');
              setSquadSearch('');
            }}
          />
        </label>

        <label className="admin-invoices-date-filter">
          <span>Invoice date from</span>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </label>

        <label className="admin-invoices-date-filter">
          <span>Invoice date to</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </label>
      </div>}

      {error && <div className="admin-invoices-error">❌ {error}</div>}

      <div className="admin-invoices-table-wrap">
        <div className="admin-invoices-table-topbar">
          <div><strong>{invoiceView === 'legacy' ? 'Legacy archive' : invoiceView === 'all' ? 'All current invoices' : invoiceView === 'attention' ? 'Needs attention' : invoiceView.replace(/_/g, ' ')}</strong>{invoiceView === 'legacy' && <small>Read-only historical records</small>}</div>
          <span className="admin-invoices-count">{filtered.length} {filtered.length === 1 ? 'invoice' : 'invoices'}</span>
        </div>
        <table className="admin-invoices-table">
          <thead>
            <tr>
              <th>Partner / invoice</th><th>Workflow</th><th>Amount</th><th>Coverage</th>
              <th>{invoiceEra === 'legacy' ? 'Sent' : 'Planned send'}</th><th>Payment due</th><th>Payment</th><th aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} style={{ padding: 16 }}>Loading invoices…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={8} className="admin-invoices-empty"><strong>No invoices in this view</strong><span>Try another work view or clear your search and filters.</span></td></tr>}
            {!loading && filtered.map(inv => {
              const derived = deriveInvoiceStatus(inv) as StatusFilter;
              const summary = summarizeInvoiceStatus(inv);
              const school = invoiceEra === 'legacy' ? undefined : schoolsById.get(String(inv.schoolId || ''))
                || schoolsByLocationId.get(String(inv.locationId || ''));
              const canDelete = invoiceEra !== 'legacy' && !(inv as any)?.sentAt;

              const coveredIds = new Set<string>();
              (inv.coveredSquadIds || []).forEach(id => coveredIds.add(String(id)));
              (inv.lineItems || []).forEach(li => { if (li?.squadId) coveredIds.add(String(li.squadId)); });
              const coveredList = Array.from(coveredIds)
                .map(id => squadsById.get(id))
                .filter(Boolean)
                .map(sq => String((sq as any)?.title || (sq as any)?.id || ''))
                .filter(Boolean);
              const coveredLocations = new Set<string>();
              if (inv.locationId) coveredLocations.add(String(inv.locationId));
              Array.from(coveredIds).forEach(id => {
                const locationId = String((squadsById.get(id) as any)?.locationId || '').trim();
                if (locationId) coveredLocations.add(locationId);
              });

              const termLabel =
                String(inv.termId || '').trim()
                  ? String(inv.termId)
                  : (inv.servicePeriodStart || inv.servicePeriodEnd)
                    ? `${fmtYmd(inv.servicePeriodStart)} → ${fmtYmd(inv.servicePeriodEnd)}`
                    : '—';

              const due = fmtYmd(inv.paymentDueDate);
              const plannedSend = fmtYmd(invoiceEra === 'legacy' ? (inv as any).sentAt || inv.invoiceDate : (inv as any).invoiceSendDate || inv.invoiceDate);
              const partnerLabel = inv.payerLegalNameSnapshot || (invoiceEra === 'legacy' ? inv.schoolNameSnapshot : school?.name || inv.schoolNameSnapshot) || 'Partner unavailable';
              const invoiceLabel = inv.internalInvoiceNumberSnapshot || programTermLabelFromId(inv.termId || '') || inv.id;
              const coverageLabel = invoiceEra === 'legacy' ? termLabel : `${coveredList.length} ${coveredList.length === 1 ? 'squad' : 'squads'}${coveredLocations.size ? ` · ${coveredLocations.size} ${coveredLocations.size === 1 ? 'location' : 'locations'}` : ''}`;
              const nextAction = invoiceEra === 'legacy' ? 'View record' : derived === 'needs_pdf' ? 'Generate PDF' : derived === 'ready_to_send' || derived === 'late_draft' ? 'Review & send' : summary.payment === 'overdue' || summary.payment === 'due_soon' || summary.payment === 'partial' ? 'Record payment' : 'Open invoice';

              return (
                <tr
                  key={inv.id}
                  className="admin-invoices-row"
                  tabIndex={0}
                  role="button"
                  onClick={() => navigate(`/app/admin/invoices/${inv.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/app/admin/invoices/${inv.id}`);
                    }
                  }}
                >
                  <td className="admin-invoices-identity"><strong>{partnerLabel}</strong><span>{invoiceLabel}</span></td>
                  <td><span className={`invoice-status-pill status-${derived === 'overdue' ? 'sent' : derived}`}>{derived === 'overdue' ? 'Sent' : derived === 'late_draft' ? 'Send delayed' : derived.replace(/_/g, ' ')}</span></td>
                  <td className="admin-invoices-amount"><strong>{fmtUsd(summary.totalCents)}</strong>{summary.balanceCents !== summary.totalCents && <span>{fmtUsd(summary.balanceCents)} open</span>}</td>
                  <td title={coveredList.join(', ') || undefined}><strong>{coverageLabel}</strong><span>{termLabel}</span></td>
                  <td>{plannedSend}</td><td>{due}</td>
                  <td><span className={`invoice-payment-pill payment-${summary.payment}`}>{summary.payment === 'not_applicable' ? (summary.totalCents === 0 ? 'No payment due' : 'Not sent') : summary.payment.replace(/_/g, ' ')}</span></td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="admin-invoices-row-actions">
                      <button
                        className="admin-invoices-open"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (nextAction === 'Record payment') navigate(`/app/admin/record-payment?invoice=${encodeURIComponent(inv.id)}`);
                          else navigate(`/app/admin/invoices/${inv.id}`);
                        }}
                      >
                        {nextAction}
                      </button>
                      {canDelete && <button className="admin-invoices-more" title="Delete draft" aria-label="Delete draft" onClick={(e) => { e.stopPropagation(); deleteDraftInvoice(inv); }}>•••</button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {confirmDelete && (
        <div className="admin-invoices-modal-overlay" onClick={() => setConfirmDelete(null)} role="presentation">
          <div className="admin-invoices-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-invoices-modal-title">
              Delete draft invoice?
            </div>
            <div className="admin-invoices-modal-body admin-invoices-modal-body--stack">
              <div>
                This will permanently delete this invoice draft and its line items.
              </div>
              <div className="admin-invoices-modal-hint">
                Invoice: <code>{confirmDelete.internalInvoiceNumberSnapshot || confirmDelete.id}</code>
              </div>
            </div>
            <div className="admin-invoices-modal-actions">
              <button className="admin-invoices-modal-cancel" onClick={() => setConfirmDelete(null)} disabled={loading}>
                Cancel
              </button>
              <button
                className="admin-invoices-modal-create"
                onClick={confirmDeleteInvoice}
                disabled={loading}
                style={{ background: 'rgba(239, 68, 68, 0.16)', borderColor: 'rgba(239, 68, 68, 0.32)', color: '#7f1d1d' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
